import { useMemo } from 'react'
import { RemoteAudioTrack, Track } from 'livekit-client'
import { useEventLiveKitAudioTrackRefs } from '@/components/livekit/EventLiveKitProvider'

/** A source's audio as the room lists it: sound coming, muted at the source, or nothing published. */
export type SourceSound = 'sound' | 'muted' | 'none'

export interface SourceAudio {
  sound: SourceSound
  /** Its track while this tab receives it (subscribed), for the host's level line; null otherwise. */
  track: RemoteAudioTrack | null
}

/**
 * What the room says of `identity`'s audio (docs/features/stage-volume.md, Honest state): whether it publishes any, and
 * whether the source muted it. A source that isn't in the room (offline) publishes none. Use inside the event's room.
 */
export function useSourceAudio(identity: string | null): SourceAudio {
  const refs = useEventLiveKitAudioTrackRefs()
  const ref = identity
    ? refs.find((candidate) => candidate.participant.identity === identity && candidate.publication.kind === Track.Kind.Audio)
    : undefined
  const published = ref?.publication.track
  const track = published instanceof RemoteAudioTrack ? published : null
  const sound: SourceSound = !ref ? 'none' : ref.publication.isMuted ? 'muted' : 'sound'
  return useMemo(() => ({ sound, track }), [sound, track])
}
